import { faLocationDot } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import { format, isValid } from 'date-fns';
import { useSetAtom } from 'jotai';

import {
  useGetPracticeGroups,
  useUpdatePracticeGroup,
} from '../../../react-query/insights/usePracticeGroups';

import { isOnUpdatePageAtom, selectedPracticeGroupAtom } from './atoms';
import { PracticeGroupActions } from './PracticeGroupActions';

export const ViewPracticeGroups = () => {
  const setSelectedPracticeGroup = useSetAtom(selectedPracticeGroupAtom);
  const setIsOnUpdatePage = useSetAtom(isOnUpdatePageAtom);

  const { data: practiceGroups } = useGetPracticeGroups();
  const { mutate: updatePracticeGroup } = useUpdatePracticeGroup();

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      <div className="vai-flex vai-flex-row vai-gap-6 vai-items-center vai-justify-between">
        <span className="vai-typography-h2 vai-text-gray-700">DSO Regions</span>
        <Button
          variant="default"
          size="sm"
          dataTestId="create-new-practice-group-button"
          onClick={() => {
            setIsOnUpdatePage(true);
            setSelectedPracticeGroup(undefined);
          }}
        >
          Create New
        </Button>
      </div>
      <span
        className="vai-typography-muted vai-w-3/4"
        data-testid="practice-groups-description"
      >
        Easily organize and manage your practices with region groups, giving
        your entire DSO seamless access. Create regions groups however you see
        fit&mdash;by region, specialty, or custom criteria&mdash;and filter them
        in the Insights dashboard.
      </span>
      <div
        className={cn(
          'vai-flex vai-flex-col vai-gap-6 vai-relative vai-w-full vai-overflow-auto',
          practiceGroups?.data?.length && 'vai-h-[calc(100vh-19rem)]',
        )}
      >
        <Table>
          <TableHeader className="vai-sticky vai-top-0 vai-z-10 vai-bg-zinc-50">
            <TableRow>
              <TableHead className="vai-w-2/5">Group Name</TableHead>
              <TableHead>Number of Practices</TableHead>
              <TableHead>Last Updated By</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead>Active Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {practiceGroups?.data?.map((group) => (
              <TableRow key={group.id}>
                <TableCell dataTestId={`practice-group-name-${group.name}`}>
                  {group.name}
                </TableCell>
                <TableCell
                  dataTestId={`practice-group-practices-length-${group.name}`}
                >
                  {group.practices.length}
                </TableCell>
                <TableCell
                  dataTestId={`practice-group-updated-by-${group.name}`}
                >
                  {group.updatedBy}
                </TableCell>
                <TableCell
                  dataTestId={`practice-group-updated-at-${group.name}`}
                >
                  {group.updatedAt &&
                    isValid(new Date(group.updatedAt)) &&
                    format(new Date(group.updatedAt), 'MM/dd/yyyy')}
                </TableCell>
                <TableCell>
                  <Switch
                    dataTestId={`practice-group-active-status-${group.name}`}
                    checked={!!group.active}
                    onCheckedChange={() => {
                      updatePracticeGroup({
                        id: group.id,
                        active: !group.active,
                        name: group.name,
                        practices: group.practices.map(
                          (practice) => practice.practiceVideaId,
                        ),
                      });
                    }}
                  />
                </TableCell>
                <TableCell>
                  <PracticeGroupActions group={group} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!practiceGroups?.data?.length && (
        <div className="vai-flex vai-items-center vai-justify-center vai-w-full vai-mt-8">
          <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-gap-4 vai-w-2/5 vai-border vai-bg-zinc-200 vai-border-zinc-300 vai-border-dashed vai-rounded-md vai-p-8">
            <span className="vai-flex vai-items-center vai-justify-center vai-h-10 vai-w-10">
              <FontAwesomeIcon
                icon={faLocationDot}
                size="2x"
                className="vai-text-zinc-600"
              />
            </span>
            <span className="vai-typography-h3 vai-text-gray-700">
              No regions added
            </span>
            <span className="vai-typography-muted vai-text-gray-500">
              Organize your practices your way
            </span>
            <Button
              className="vai-w-48"
              variant="default"
              size="sm"
              dataTestId="create-new-practice-group-empty-state-button"
              onClick={() => {
                setIsOnUpdatePage(true);
                setSelectedPracticeGroup(undefined);
              }}
            >
              Create New
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
